/**
 * Masters → Parties. Everything about a party, in one place.
 * @file src/web/features/admin/components/masters/ComplianceTab.tsx
 *
 * This was two tabs — "Parties" (Tally's ledger: GSTIN, place of supply,
 * opening balance) and "Compliance" (the app's: PAN, MSME, TDS, Sec 197).
 * That seam was a database boundary, not anything a CA thinks about: they
 * have one party and wanted one screen. Merged.
 *
 * The distinction survives as a LABEL, not a tab, because it still matters:
 * a Tally-owned field is replaced by the next import unless the ledger is
 * protected — and protection in tally.loader is per LEDGER, not per field,
 * so editing one Tally column freezes the whole record against future
 * imports. The drawer says so where it is true.
 */

import { useEffect, useState } from 'react';
import { Users2, Pencil, X, AlertTriangle, ShieldCheck, ChevronDown } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import {
  Card, Field, TextInput, Select, PrimaryBtn, GhostBtn, FormError,
  Loading, ErrBox, EmptyState, Th, Td, Pill, useEntityResource, mutate,
} from './shared';
import { PartyRecords } from './PartyRecords';

interface Profile {
  pan: string | null; panSource: string | null; panGstinMismatch: boolean;
  constitution: string | null;
  msmeStatus: string | null; udyamNumber: string | null; udyamRegisteredOn: string | null;
  paymentDueDays: number | null;
  tdsSection: string | null; tdsNatureOfPayment: string | null;
  tdsThreshold: string | null; tdsRate: string | null;
  lowerDeductionCertNo: string | null; lowerDeductionRate: string | null;
  lowerDeductionValidFrom: string | null; lowerDeductionValidTo: string | null;
  relatedParty: boolean; relatedPartyNote: string | null;
  ecommerceOperator: boolean; ecommerceGstin: string | null;
  tradeName: string | null;
  foreignParty: boolean; countryCode: string | null; currencyCode: string | null;
  exportType: string | null;
  sezStatus: string | null; reverseChargeApplicable: boolean; customerCategory: string | null;
  tcsApplicable: boolean; tcsSection: string | null; tcsRate: string | null;
  partyKind: string | null; supplyType: string | null; gstRegistrationStatus: string | null;
  creditPeriodDays: number | null; creditLimit: string | null;
  openingBalance: string | null; openingBalanceType: string | null; openingBalanceAsOn: string | null;
}
interface Party {
  ledgerId: string; name: string; parent: string | null;
  kind: 'customer' | 'supplier';
  // Tally's — replaced on the next import unless `edited` protects the ledger
  gstin: string | null; gstRegistrationType: string | null;
  gstTypeOfSupply: string | null; placeOfSupply: string | null;
  tallyOpeningBalance: string | null; source: string; edited: boolean;
  profile: Profile | null;
  gaps: string[];
}

const SEZ_STATUSES = [
  { value: 'sez_unit', label: 'SEZ unit' },
  { value: 'sez_developer', label: 'SEZ developer' },
  { value: 'deemed_export', label: 'Deemed export' },
];
const CUSTOMER_CATEGORIES = ['B2B', 'B2C', 'SEZ', 'DEEMED_EXPORT', 'EXPORT', 'GOVERNMENT'];
const EXPORT_TYPES = [
  { value: 'WPAY', label: 'With payment of tax' },
  { value: 'WOPAY', label: 'Without payment (LUT)' },
];
const TCS_SECTIONS = ['206C(1H)', '206C(1)', '206C(1G)'];
const PARTY_KINDS = [
  { value: 'goods', label: 'Goods' },
  { value: 'services', label: 'Services' },
  { value: 'both', label: 'Both' },
];
const SUPPLY_TYPES = [
  { value: 'regular', label: 'Regular' },
  { value: 'composite', label: 'Composite — taxed at the principal rate' },
  { value: 'mixed', label: 'Mixed — taxed at the highest rate' },
];
const GST_REG_STATUSES = [
  { value: 'registered', label: 'Registered' },
  { value: 'composition', label: 'Composition' },
  { value: 'unregistered', label: 'Unregistered' },
  { value: 'consumer', label: 'Consumer / B2C' },
];

const GAP_LABEL: Record<string, string> = {
  PARTY_NO_GSTIN: 'GSTIN',
  PARTY_NO_PROFILE: 'not started',
  PARTY_NO_PAN: 'PAN',
  PARTY_PAN_MISMATCH: 'PAN ≠ GSTIN',
  PARTY_NO_CONSTITUTION: 'constitution',
  PARTY_MSME_UNKNOWN: 'MSME',
  PARTY_FOREIGN_NO_COUNTRY: 'country',
  PARTY_NO_PLACE_OF_SUPPLY: 'place of supply',
  PARTY_NO_REG_STATUS: 'registration status',
  PARTY_NO_BANK: 'bank account',
  PARTY_FOREIGN_NO_CURRENCY: 'currency',
  PARTY_EXPORT_NO_TYPE: 'export type',
  PARTY_TCS_INCOMPLETE: 'TCS section/rate',
  PARTY_OPENING_NO_TYPE: 'opening Dr/Cr',
  PARTY_LDC_EXPIRED: 'Sec 197 cert expired',
};

const CONSTITUTIONS = ['Individual', 'HUF', 'Company', 'Partnership', 'LLP', 'AOP', 'BOI', 'Trust', 'Government', 'Local Authority'];
const MSME_STATUSES = ['none', 'micro', 'small', 'medium'];

export function ComplianceTab({ entityId }: { entityId: string | null }) {
  const [gapsOnly, setGapsOnly] = useState(false);
  const [kind, setKind] = useState<'customer' | 'supplier' | ''>('');
  const [q, setQ] = useState('');
  const { items, loading, error, reload } = useEntityResource<Party>(
    `parties?gapsOnly=${gapsOnly}${kind ? `&kind=${kind}` : ''}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
    entityId,
    [gapsOnly, kind, q],
  );
  const [editing, setEditing] = useState<Party | null>(null);

  if (loading) return <Loading label="Loading party compliance" />;
  if (error) return <ErrBox msg={error} />;

  return (
    <Card
      title="Parties"
      subtitle="Every customer and supplier, and everything recorded against them. Fields from Tally are marked as such and refresh on each import; the rest are the app's own and survive it."
      action={
        <div className="flex items-center gap-3">
          <input
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search parties…"
            className="rounded-xl border border-ink-200 bg-white px-3 py-1.5 text-sm focus:border-ink-300 focus-visible:outline-none"
          />
          <label className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-xs font-medium text-ink-600">
            <input type="checkbox" checked={gapsOnly} onChange={(e) => setGapsOnly(e.target.checked)} />
            Only incomplete
          </label>
          <div className="inline-flex rounded-xl border border-ink-200 bg-white p-1">
            {([['', 'All'], ['customer', 'Customers'], ['supplier', 'Suppliers']] as const).map(([v, label]) => (
              <button
                key={label}
                onClick={() => setKind(v)}
                className={`rounded-lg px-3 py-1 text-xs font-semibold transition ${
                  kind === v ? 'bg-inkblack text-paper' : 'text-ink-600 hover:bg-ink-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      }
    >
      {editing && (
        <ComplianceForm
          party={editing}
          entityId={entityId}
          onCancel={() => setEditing(null)}
          onSave={async (body) => {
            await mutate(`parties/${editing.ledgerId}`, 'PATCH', { entityId, ...body });
            setEditing(null); toast.success('Compliance saved'); reload();
          }}
        />
      )}

      {items.length === 0 ? (
        <EmptyState
          what={gapsOnly ? 'incomplete party' : 'party'}
          why={gapsOnly
            ? 'Every party has a PAN, a constitution and an MSME status on record. Untick "only incomplete" to see them all.'
            : 'Parties appear once books are imported — they are the ledgers under Sundry Debtors and Sundry Creditors.'}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <Th>Party</Th><Th>GSTIN</Th><Th>PAN</Th>
                <Th>Constitution</Th><Th>MSME</Th><Th>Missing</Th><Th right stick>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {items.map((p) => (
                <tr
                  key={p.ledgerId}
                  onClick={() => setEditing(p)}
                  className="cursor-pointer hover:bg-ink-50/50"
                >
                  <Td>
                    <span className="inline-flex items-center gap-2">
                      <Users2 className="h-3.5 w-3.5 text-ink-400" />
                      <span>
                        {p.name}
                        <span className="ml-1.5 text-[11px] text-ink-400">{p.kind}</span>
                      </span>
                    </span>
                  </Td>
                  <Td mono>{p.gstin ?? <span className="text-warn-600">—</span>}</Td>
                  <Td mono>
                    {p.profile?.pan
                      ? (
                        <span className="inline-flex items-center gap-1.5">
                          {p.profile.pan}
                          {p.profile.panSource === 'derived' && <Pill tone="neutral">derived</Pill>}
                          {p.profile.panGstinMismatch && <Pill tone="danger"><AlertTriangle className="h-3 w-3" /> mismatch</Pill>}
                        </span>
                      )
                      : <span className="text-warn-600">—</span>}
                  </Td>
                  <Td muted>{p.profile?.constitution ?? <span className="text-warn-600">—</span>}</Td>
                  <Td>
                    {p.profile?.msmeStatus
                      ? <Pill tone={p.profile.msmeStatus === 'none' ? 'neutral' : 'warn'}>{p.profile.msmeStatus}</Pill>
                      : <span className="text-warn-600">not checked</span>}
                  </Td>
                  <Td>
                    {p.gaps.length === 0
                      ? <Pill tone="ok">complete</Pill>
                      : <span className="text-[11px] text-warn-700">{p.gaps.map((g) => GAP_LABEL[g] ?? g).join(', ')}</span>}
                  </Td>
                  <Td right stick>
                    <GhostBtn onClick={() => setEditing(p)}><Pencil className="h-3 w-3" /> Edit</GhostBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/**
 * A folding block inside the party modal. The shared `Card` is sized for a
 * page; this is the same idea at modal scale.
 *
 * `filled` is what the bar says while shut. Ten sections of mostly-empty
 * compliance fields is a long scroll, and folding them only helps if a shut
 * section still says whether anything is in it — otherwise you open all ten
 * to find the two that matter.
 */
function Fold({ title, filled, defaultOpen = false, children }: {
  title: string; filled?: React.ReactNode; defaultOpen?: boolean; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-ink-200">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left transition hover:bg-ink-50/60"
      >
        <ChevronDown className={`h-4 w-4 shrink-0 text-ink-400 transition-transform ${open ? '' : '-rotate-90'}`} aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-wide text-ink-600">{title}</span>
        {filled != null && <span className="truncate text-xs font-normal text-ink-500">{filled}</span>}
      </button>
      <div className={open ? 'border-t border-ink-100 px-4 py-4' : 'hidden'}>{children}</div>
    </div>
  );
}

function ComplianceForm({ party, entityId, onSave, onCancel }: {
  party: Party; entityId: string | null;
  onSave: (body: Record<string, unknown>) => Promise<void>; onCancel: () => void;
}) {
  const [recordCount, setRecordCount] = useState<{ addresses: number; bank: number } | null>(null);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onCancel]);
  const p = party.profile;
  const d = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : '');
  const [f, setF] = useState({
    pan: p?.pan ?? '',
    constitution: p?.constitution ?? '',
    msmeStatus: p?.msmeStatus ?? '',
    udyamNumber: p?.udyamNumber ?? '',
    udyamRegisteredOn: d(p?.udyamRegisteredOn),
    paymentDueDays: p?.paymentDueDays != null ? String(p.paymentDueDays) : '',
    tdsSection: p?.tdsSection ?? '',
    tdsNatureOfPayment: p?.tdsNatureOfPayment ?? '',
    tdsRate: p?.tdsRate ?? '',
    tdsThreshold: p?.tdsThreshold ?? '',
    lowerDeductionCertNo: p?.lowerDeductionCertNo ?? '',
    lowerDeductionRate: p?.lowerDeductionRate ?? '',
    lowerDeductionValidFrom: d(p?.lowerDeductionValidFrom),
    lowerDeductionValidTo: d(p?.lowerDeductionValidTo),
    tradeName: p?.tradeName ?? '',
    foreignParty: p?.foreignParty ?? false,
    countryCode: p?.countryCode ?? '',
    currencyCode: p?.currencyCode ?? '',
    exportType: p?.exportType ?? '',
    sezStatus: p?.sezStatus ?? '',
    reverseChargeApplicable: p?.reverseChargeApplicable ?? false,
    customerCategory: p?.customerCategory ?? '',
    tcsApplicable: p?.tcsApplicable ?? false,
    tcsSection: p?.tcsSection ?? '',
    tcsRate: p?.tcsRate ?? '',
    creditPeriodDays: p?.creditPeriodDays != null ? String(p.creditPeriodDays) : '',
    creditLimit: p?.creditLimit ?? '',
    partyKind: p?.partyKind ?? '',
    supplyType: p?.supplyType ?? '',
    gstRegistrationStatus: p?.gstRegistrationStatus ?? '',
    openingBalance: p?.openingBalance ?? '',
    openingBalanceType: p?.openingBalanceType ?? '',
    openingBalanceAsOn: d(p?.openingBalanceAsOn),
    relatedParty: p?.relatedParty ?? false,
    relatedPartyNote: p?.relatedPartyNote ?? '',
    ecommerceOperator: p?.ecommerceOperator ?? false,
    ecommerceGstin: p?.ecommerceGstin ?? '',
  });
  const set = (k: keyof typeof f) => (v: string | boolean) => setF((s) => ({ ...s, [k]: v }));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await onSave({
        ...f,
        paymentDueDays: f.paymentDueDays === '' ? null : Number(f.paymentDueDays),
        creditPeriodDays: f.creditPeriodDays === '' ? null : Number(f.creditPeriodDays),
      });
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-6"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      {/* The BODY scrolls, not the overlay. Sticky bars inside a scrolling
          overlay stick to the overlay's edges, not the card's — so the p-6
          gutter above and below let content slide past them, and the card's
          rounded corners scrolled away with it. A capped flex column puts the
          header and footer outside the scroll container, where nothing can
          pass behind them. */}
      <div
        className="flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-ink-100 bg-white px-6 py-5">
          <div>
            <h3 className="text-base font-semibold text-ink-900">
              {party.name}
              <span className="ml-2 text-xs font-normal text-ink-400">{party.kind}</span>
            </h3>
            {party.gstin && <p className="mt-0.5 font-mono text-xs text-ink-500">{party.gstin}</p>}
          </div>
          <button onClick={onCancel} className="rounded-full p-1 text-ink-400 hover:bg-ink-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
      <FormError msg={err} />

      {/* Tally's half. Read-only here on purpose: these columns come from the
          client's own books, and the place to correct them is Tally. Editing
          one in the app sets Ledger.edited, and tally.loader then skips the
          WHOLE ledger on every future import — name, parent and opening
          balance included. That is a big consequence for a typo fix, so it is
          not offered behind an innocent-looking input. */}
      <div className="mb-5 rounded-xl border border-ink-200 bg-white px-4 py-3">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">From Tally</p>
          {party.edited
            ? <Pill tone="warn">edited — this ledger no longer updates from Tally</Pill>
            : <Pill tone="neutral">updates on every import</Pill>}
        </div>
        <div className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-[11px] text-ink-400">GSTIN</p>
            <p className="font-mono text-ink-700">{party.gstin ?? <span className="font-sans text-warn-600">not on file</span>}</p>
          </div>
          <div>
            <p className="text-[11px] text-ink-400">Registration type</p>
            <p className="text-ink-700">{party.gstRegistrationType ?? '—'}</p>
          </div>
          <div>
            <p className="text-[11px] text-ink-400">Place of supply</p>
            <p className="text-ink-700">{party.placeOfSupply ?? '—'}</p>
          </div>
          <div>
            <p className="text-[11px] text-ink-400">Type of supply</p>
            <p className="text-ink-700">{party.gstTypeOfSupply ?? '—'}</p>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-ink-400">
          Correct these in Tally and re-import. Editing them here would freeze the whole ledger against
          future imports, which costs more than it fixes.
        </p>
      </div>

      <Fold title="Identity" defaultOpen={true} filled={[f.pan, f.constitution, f.tradeName].filter(Boolean).length + ' of 3'}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="PAN" hint={party.gstin ? 'Checked against the GSTIN, which embeds it at characters 3-12.' : undefined}>
          <TextInput value={f.pan} onChange={(v) => set('pan')(v.toUpperCase())} mono />
        </Field>
        <Field label="Constitution" hint="TDS rates and sections differ by constitution.">
          <Select value={f.constitution} onChange={set('constitution')} options={CONSTITUTIONS} />
        </Field>
        <Field label="Trade name" hint="The name the party trades under, if it differs from the ledger name.">
          <TextInput value={f.tradeName} onChange={set('tradeName')} />
        </Field>
      </div>
      </Fold>

      <Fold title="MSME — Section 43B(h)" defaultOpen={true} filled={f.msmeStatus || 'not checked'}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="MSME status" hint="Payments to micro/small suppliers beyond the window become disallowed expenditure.">
          <Select value={f.msmeStatus} onChange={set('msmeStatus')} options={MSME_STATUSES} />
        </Field>
        <Field label="Udyam number"><TextInput value={f.udyamNumber} onChange={set('udyamNumber')} mono /></Field>
        <Field label="Registered on"><TextInput type="date" value={f.udyamRegisteredOn} onChange={set('udyamRegisteredOn')} /></Field>
        <Field label="Payment window (days)" hint="45 with a written agreement, otherwise 15.">
          <TextInput value={f.paymentDueDays} onChange={set('paymentDueDays')} placeholder="45" mono />
        </Field>
      </div>
      </Fold>

      <Fold title="TDS" defaultOpen={true} filled={f.tdsSection || 'no section set'}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Section"><TextInput value={f.tdsSection} onChange={set('tdsSection')} placeholder="194J" /></Field>
        <Field label="Nature of payment" hint="TRACES expects its standardised code, not the ledger name.">
          <TextInput value={f.tdsNatureOfPayment} onChange={set('tdsNatureOfPayment')} />
        </Field>
        <Field label="Rate %"><TextInput value={f.tdsRate} onChange={set('tdsRate')} mono /></Field>
        <Field label="Threshold"><TextInput value={f.tdsThreshold} onChange={set('tdsThreshold')} mono /></Field>
      </div>
      </Fold>

      <Fold title="Sec 197 certificate" defaultOpen={false} filled={f.lowerDeductionCertNo || 'none'}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Certificate number"><TextInput value={f.lowerDeductionCertNo} onChange={set('lowerDeductionCertNo')} mono /></Field>
        <Field label="Rate %" hint="Overrides the section rate while valid."><TextInput value={f.lowerDeductionRate} onChange={set('lowerDeductionRate')} mono /></Field>
        <Field label="Valid from"><TextInput type="date" value={f.lowerDeductionValidFrom} onChange={set('lowerDeductionValidFrom')} /></Field>
        <Field label="Valid to"><TextInput type="date" value={f.lowerDeductionValidTo} onChange={set('lowerDeductionValidTo')} /></Field>
      </div>
      </Fold>

      <Fold
        title="Supply &amp; registration"
        filled={[
          f.partyKind && PARTY_KINDS.find((k) => k.value === f.partyKind)?.label,
          f.supplyType && f.supplyType,
          f.gstRegistrationStatus && f.gstRegistrationStatus,
        ].filter(Boolean).join(' · ') || 'not set'}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Supplies" hint="Decides whether a line carries an HSN or a SAC.">
            <Select value={f.partyKind} onChange={set('partyKind')} options={PARTY_KINDS} placeholder="Not set" />
          </Field>
          <Field label="Type of supply" hint="Composite is taxed at the principal supply's rate; mixed at the highest in the bundle.">
            <Select value={f.supplyType} onChange={set('supplyType')} options={SUPPLY_TYPES} placeholder="Not set" />
          </Field>
          <Field
            label="Registration status"
            hint={party.gstRegistrationType ? `Tally says: ${party.gstRegistrationType}` : 'Tally has no value for this party.'}
          >
            <Select value={f.gstRegistrationStatus} onChange={set('gstRegistrationStatus')} options={GST_REG_STATUSES} placeholder="Not set" />
          </Field>
        </div>
      </Fold>

      <Fold title="Cross-border" defaultOpen={false} filled={f.foreignParty ? (f.countryCode || 'foreign, country missing') : 'domestic'}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Foreign party" hint="Supplier: non-resident TDS, DTAA and Form 15CA/15CB. Customer: the supply is an export.">
          <label className="inline-flex items-center gap-2 pt-1.5 text-sm text-ink-700">
            <input type="checkbox" checked={f.foreignParty} onChange={(e) => set('foreignParty')(e.target.checked)} />
            Yes
          </label>
        </Field>
        <Field label="Country" hint="2-letter ISO code. Decides the DTAA rate.">
          <TextInput value={f.countryCode} onChange={(v) => set('countryCode')(v.toUpperCase())} placeholder="AE" mono />
        </Field>
        <Field label="Currency" hint="3-letter ISO code.">
          <TextInput value={f.currencyCode} onChange={(v) => set('currencyCode')(v.toUpperCase())} placeholder="USD" mono />
        </Field>
        <Field label="Export type" hint="Customers only. Pre-fills GSTR-1 Table 6A.">
          <Select value={f.exportType} onChange={set('exportType')} options={EXPORT_TYPES} placeholder="Not set" />
        </Field>
      </div>
      </Fold>

      <Fold title="GST treatment" defaultOpen={false} filled={[f.sezStatus, f.customerCategory].filter(Boolean).join(', ') || (f.reverseChargeApplicable ? 'RCM' : 'defaults not set')}>
      <p className="mb-3 max-w-3xl text-[11px] text-ink-400">
        Defaults that pre-fill a voucher. The voucher stays the source of truth for what is filed — a
        return reads the voucher, never this.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="SEZ / deemed export">
          <Select value={f.sezStatus} onChange={set('sezStatus')} options={SEZ_STATUSES} placeholder="Not applicable" />
        </Field>
        <Field label="Reverse charge" hint="Supplies from this party attract RCM.">
          <label className="inline-flex items-center gap-2 pt-1.5 text-sm text-ink-700">
            <input type="checkbox" checked={f.reverseChargeApplicable} onChange={(e) => set('reverseChargeApplicable')(e.target.checked)} />
            Yes
          </label>
        </Field>
        <Field label="Customer category" hint="Customers only.">
          <Select value={f.customerCategory} onChange={set('customerCategory')} options={CUSTOMER_CATEGORIES} placeholder="Not set" />
        </Field>
      </div>
      </Fold>

      <Fold title="TCS &amp; credit terms" defaultOpen={false} filled={f.tcsApplicable ? (f.tcsSection || 'applicable') : (f.creditPeriodDays ? f.creditPeriodDays + ' days credit' : 'not set')}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="TCS applicable" hint="Section 206C — collected FROM this customer, the mirror of TDS.">
          <label className="inline-flex items-center gap-2 pt-1.5 text-sm text-ink-700">
            <input type="checkbox" checked={f.tcsApplicable} onChange={(e) => set('tcsApplicable')(e.target.checked)} />
            Yes
          </label>
        </Field>
        <Field label="TCS section">
          <Select value={f.tcsSection} onChange={set('tcsSection')} options={TCS_SECTIONS} placeholder="Not set" />
        </Field>
        <Field label="TCS rate %"><TextInput value={String(f.tcsRate ?? '')} onChange={set('tcsRate')} mono /></Field>
        <div />
        <Field label="Credit period (days)"><TextInput value={f.creditPeriodDays} onChange={set('creditPeriodDays')} mono /></Field>
        <Field label="Credit limit"><TextInput value={String(f.creditLimit ?? '')} onChange={set('creditLimit')} mono /></Field>
      </div>
      </Fold>

      <Fold title="Opening balance" defaultOpen={false} filled={f.openingBalance ? f.openingBalance + ' ' + (f.openingBalanceType || '') : 'not recorded'}>
      <p className="mb-3 max-w-3xl text-[11px] text-ink-400">
        The app&rsquo;s own figure. Tally&rsquo;s is shown beside it and is overwritten on every re-import —
        this one is not. Amount is unsigned; the side says the direction.
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Amount"><TextInput value={String(f.openingBalance ?? '')} onChange={set('openingBalance')} mono /></Field>
        <Field label="Dr / Cr">
          <Select value={f.openingBalanceType} onChange={set('openingBalanceType')}
            options={[{ value: 'Dr', label: 'Dr — receivable' }, { value: 'Cr', label: 'Cr — payable' }]} placeholder="Not set" />
        </Field>
        <Field label="As on" hint="Normally the first day of the financial year.">
          <TextInput type="date" value={f.openingBalanceAsOn} onChange={set('openingBalanceAsOn')} />
        </Field>
        <Field label="Tally says" hint="Read-only. Replaced on the next import.">
          <div className="rounded-xl border border-ink-100 bg-ink-50 px-3 py-2 font-mono text-sm text-ink-600">
            {party.tallyOpeningBalance
              ? Number(party.tallyOpeningBalance).toLocaleString('en-IN')
              : <span className="font-sans text-ink-400">not imported</span>}
          </div>
        </Field>
      </div>
      </Fold>

      <Fold
        title="Addresses &amp; bank"
        filled={recordCount ? `${recordCount.addresses} address${recordCount.addresses === 1 ? '' : 'es'}, ${recordCount.bank} account${recordCount.bank === 1 ? '' : 's'}` : undefined}
      >
        <PartyRecords entityId={entityId} ledgerId={party.ledgerId} gstin={party.gstin} onCount={setRecordCount} />
      </Fold>

      <Fold title="Other" defaultOpen={false} filled={[f.relatedParty && 'related party', f.ecommerceOperator && 'e-commerce operator'].filter(Boolean).join(', ') || 'none'}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Related party" hint="Section 40A(2) and audit disclosure.">
          <label className="inline-flex items-center gap-2 pt-1.5 text-sm text-ink-700">
            <input type="checkbox" checked={f.relatedParty} onChange={(e) => set('relatedParty')(e.target.checked)} />
            Yes
          </label>
        </Field>
        <Field label="Note"><TextInput value={f.relatedPartyNote} onChange={set('relatedPartyNote')} /></Field>
        <Field label="E-commerce operator" hint="Supplies through this party carry its GSTIN as `etin`.">
          <label className="inline-flex items-center gap-2 pt-1.5 text-sm text-ink-700">
            <input type="checkbox" checked={f.ecommerceOperator} onChange={(e) => set('ecommerceOperator')(e.target.checked)} />
            Yes
          </label>
        </Field>
        <Field label="Operator GSTIN"><TextInput value={f.ecommerceGstin} onChange={(v) => set('ecommerceGstin')(v.toUpperCase())} mono /></Field>
      </div>
      </Fold>

        </div>

        {/* Outside the scroll area, so Save is always on screen. */}
        <div className="flex shrink-0 items-center gap-3 border-t border-ink-100 bg-white px-6 py-4">
          <PrimaryBtn onClick={submit} busy={busy}><ShieldCheck className="h-4 w-4" /> Save party</PrimaryBtn>
          <button onClick={onCancel} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
        </div>
      </div>
    </div>
  );
}
